/**
 * 단일 선택 상태머신 — N개의 id 중 정확히 하나만 활성. 탭/세그먼트/라디오 등 "한 번에 하나만 표시"
 * UI의 가시성 SSoT(single source of truth)로 사용한다. Phaser/DOM 등 렌더 의존성 0의 순수 로직.
 *
 * 사용 예: 탭 콘텐츠 가시성을 이 클래스로 파생하면 "초기엔 하나만 활성 / 동시에 둘 이상 활성 불가"
 * 불변식이 코드로 강제되어, 각 뷰의 `setVisible`를 boolean 하드코딩하던 누락/겹침 위험이 사라진다.
 */
export class SingleSelection<Id extends string> {
  private readonly ids: readonly Id[];
  private activeId: Id;

  /**
   * @param ids   선택 후보 id 목록 (중복은 허용하나 의미 없음).
   * @param initial 초기 활성 id. `ids`에 없으면 throw (오타·잘못된 초기값 조기 차단).
   */
  constructor(ids: readonly Id[], initial: Id) {
    if (!ids.includes(initial)) {
      throw new Error(`SingleSelection: initial id "${initial}" is not in ids [${ids.join(", ")}]`);
    }
    this.ids = ids;
    this.activeId = initial;
  }

  /** 현재 활성 id. */
  get active(): Id {
    return this.activeId;
  }

  /**
   * 활성 id 변경. 유효한 id면 활성으로 바꾸고 `true`, `ids`에 없는 id면 상태를 바꾸지 않고 `false`.
   * (동일 id 재선택도 `true` — 멱등.)
   */
  select(id: Id): boolean {
    if (!this.ids.includes(id)) return false;
    this.activeId = id;
    return true;
  }

  /** 주어진 id가 현재 활성인지. */
  isActive(id: Id): boolean {
    return this.activeId === id;
  }

  /** 각 id → 활성 여부 맵. 정확히 하나(activeId)만 `true`. 뷰 가시성 일괄 적용용. */
  visibility(): Record<Id, boolean> {
    const map = {} as Record<Id, boolean>;
    for (const id of this.ids) map[id] = id === this.activeId;
    return map;
  }
}
